import React, { useEffect, useRef } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Stack, Typography } from '@mui/material';

import { useFilterMenuTokens } from '../filterMenuTokens';
import type { BooleanFieldDef } from '../types';

interface BooleanEditorPanelProps {
  def: BooleanFieldDef;
  value: boolean | undefined;
  /** Terminal: the popover applies the value and closes the whole menu. */
  onSelect: (value: boolean) => void;
  autoFocus: boolean;
}

/** Two-choice editor for boolean filters. */
export const BooleanEditorPanel: React.FC<BooleanEditorPanelProps> = ({
  def,
  value,
  onSelect,
  autoFocus,
}) => {
  const tokens = useFilterMenuTokens();
  const firstRowRef = useRef<HTMLButtonElement>(null);
  const secondRowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (autoFocus) firstRowRef.current?.focus();
  }, [autoFocus]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const target =
      document.activeElement === firstRowRef.current ? secondRowRef : firstRowRef;
    target.current?.focus();
  };

  const rows: Array<{ choice: boolean; label: string; ref: React.RefObject<HTMLButtonElement | null> }> = [
    { choice: true, label: def.trueLabel ?? 'Yes', ref: firstRowRef },
    { choice: false, label: def.falseLabel ?? 'No', ref: secondRowRef },
  ];

  return (
    <Box sx={{ py: 0.5 }} onKeyDown={handleKeyDown}>
      {rows.map(row => (
        <Stack
          component="button"
          type="button"
          direction="row"
          key={row.label}
          ref={row.ref}
          onClick={() => onSelect(row.choice)}
          sx={{
            alignItems: 'center',
            bgcolor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            gap: 1,
            height: tokens.rowHeight,
            px: 1.5,
            textAlign: 'left',
            width: '100%',
            '&:hover, &:focus-visible': { bgcolor: tokens.hoverBg },
          }}
        >
          <Box sx={{ width: 16 }}>
            {value === row.choice && <CheckIcon sx={{ color: tokens.ink, fontSize: 14 }} />}
          </Box>
          <Typography sx={{ color: tokens.ink, fontSize: tokens.fontSize }}>
            {row.label}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};
